import {
  Box,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Icon from '@mdi/react'
import { mdiAlertCircleOutline, mdiEyeOutline, mdiRestore } from '@mdi/js'

import {
  fetchInvoice,
  fetchInvoices,
  reprocessInvoice,
} from '../SubscriptionActions'
import {
  StyledTableCellBody,
  StyledTableCellHeader,
} from '../../../components/hooks/BaseTableComponents'
import { exportFormatedMoney } from '../../../util/utils'
import colors from '../../../assets/colors'

import InvoiceDetails from './InvoiceDetailsModal'
import Filter from './PaymentHistoryFilter'
import StatusChip, { getInvoiceStatusTone } from './Forms/StatusChip'
import ConfirmModal from './Forms/ConfirmModal'

const PaymentHistory = () => {
  const dispatch = useDispatch()

  const invoices = useSelector(state => state.subscription.invoices)
  const planDetails = useSelector(state => state.subscription.planDetails)

  const [open, setOpen] = useState(false)
  // confirmação em aberto (reprocessar fatura)
  const [confirmRequest, setConfirmRequest] = useState(null)
  // id da fatura sendo reprocessada agora (cancel + create no gateway, não é instantâneo)
  const [reprocessingId, setReprocessingId] = useState(null)

  const handleClose = () => {
    setOpen(!open)
  }

  useEffect(() => {
    dispatch(fetchInvoices())
  }, [])

  const handleViewInvoice = id => {
    dispatch(fetchInvoice(id))
    handleClose()
  }

  // fatura falhou no gateway
  const hasFailureAlert = item => item?.status_id == 40

  // fatura de boleto, ainda não paga nem cancelada
  const canViewInvoice = item =>
    item?.payment_method_id == 20 && ![40, 30].includes(item?.status_id)

  // fatura falhou, ainda não foi reprocessada e a assinatura não está cancelada
  const canReprocess = item =>
    item?.status_id == 40 &&
    !item?.reprocessed &&
    planDetails?.status?.var_id != 40

  const handleReprocessInvoice = item => {
    setConfirmRequest({
      title: 'Reprocessar fatura?',
      description: `Deseja reprocessar a fatura de R$ ${exportFormatedMoney(
        item?.value
      )} via ${item?.payment_method}?`,
      confirmationText: 'Sim, reprocessar',
      onConfirm: async () => {
        setReprocessingId(item?.id)
        await dispatch(reprocessInvoice(item?.id))
        await dispatch(fetchInvoices())
        setReprocessingId(null)
      },
    })
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Filter />
      </Grid>
      <Grid item xs={12}>
        <TableContainer>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <StyledTableCellHeader>Plano</StyledTableCellHeader>
                <StyledTableCellHeader align="center">
                  Valor
                </StyledTableCellHeader>
                <StyledTableCellHeader>
                  Método de Pagamento
                </StyledTableCellHeader>
                <StyledTableCellHeader align="center" sortDirection={false}>
                  Data de Referência
                </StyledTableCellHeader>
                <StyledTableCellHeader>
                  Data de Vencimento
                </StyledTableCellHeader>
                <StyledTableCellHeader align="center">
                  Status
                </StyledTableCellHeader>
                <StyledTableCellHeader align="right">
                  Ações
                </StyledTableCellHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoices.map((item, index) => (
                <TableRow key={index}>
                  <StyledTableCellBody>{item.client_plan}</StyledTableCellBody>
                  <StyledTableCellBody align="center">
                    R$ {exportFormatedMoney(item.value)}
                  </StyledTableCellBody>
                  <StyledTableCellBody>
                    {item.payment_method}
                  </StyledTableCellBody>
                  <StyledTableCellBody align="center">
                    {item.reference_date}
                  </StyledTableCellBody>
                  <StyledTableCellBody>{item.due_at}</StyledTableCellBody>
                  <StyledTableCellBody align="center">
                    {item?.reprocessed ? (
                      <StatusChip label="Reprocessada" tone="neutral" />
                    ) : (
                      <StatusChip
                        label={item?.status_value}
                        tone={getInvoiceStatusTone(
                          item?.status_id,
                          item?.status_value
                        )}
                      />
                    )}
                  </StyledTableCellBody>
                  <StyledTableCellBody align="right">
                    <Box
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: '4px',
                      }}
                    >
                      {hasFailureAlert(item) && (
                        <Tooltip
                          title={
                            item?.failure_reason ||
                            'Motivo não informado pelo gateway'
                          }
                        >
                          <span style={{ display: 'inline-flex' }}>
                            <Icon
                              path={mdiAlertCircleOutline}
                              size={1}
                              color={colors.error}
                            />
                          </span>
                        </Tooltip>
                      )}
                      {canViewInvoice(item) && (
                        <Tooltip title="Ver fatura">
                          <span style={{ display: 'inline-flex' }}>
                            <IconButton
                              style={{ padding: '0px ' }}
                              onClick={() => {
                                handleViewInvoice(item?.id)
                              }}
                              aria-label="ver fatura"
                            >
                              <Icon path={mdiEyeOutline} size={1} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      {canReprocess(item) && (
                        <Tooltip title="Reprocessar fatura">
                          <span style={{ display: 'inline-flex' }}>
                            <IconButton
                              style={{
                                padding: ' 0px',
                                opacity: reprocessingId === item?.id ? 0.5 : 1,
                              }}
                              disabled={reprocessingId === item?.id}
                              onClick={() => {
                                handleReprocessInvoice(item)
                              }}
                              aria-label="reprocessar fatura"
                            >
                              <Icon path={mdiRestore} size={1} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </Box>
                  </StyledTableCellBody>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>

      <InvoiceDetails open={open} handleClose={handleClose} />

      <ConfirmModal
        confirm={confirmRequest}
        onClose={() => setConfirmRequest(null)}
      />
    </Grid>
  )
}

export default PaymentHistory
